import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ChevronLeft, MapPin, Calendar, Award, ChevronRight, ImageIcon, ShieldCheck } from "lucide-react";
import MarkdownContent from "../components/MarkdownContent";
import { trackEvent } from "../lib/analytics";
import { resolveImageUrl } from "../lib/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function PhotoLightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div onClick={onClose} data-testid="brand-lightbox"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <img src={resolveImageUrl(src)} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "4px" }} />
    </div>
  );
}
import { useSeo } from "../lib/seo";

export default function BrandDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState("");

  useSeo(brand ? {
    title: `${String(brand.name).trim()} в Алматы — ANGIOLINI`,
    description: (brand.short_description
      ? `${String(brand.name).trim()}: ${brand.short_description}. Модели в каталоге ANGIOLINI, Алматы.`
      : `${String(brand.name).trim()} в бутике ANGIOLINI, Алматы: модели каталога с ценами в тенге.`).slice(0, 300),
    canonical: `/app/brand/${brand.slug || slug}`,
  } : {});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const b = await axios.get(`${API}/brands/${slug}`);
        setBrand(b.data);
        trackEvent("brand_view", { target_id: b.data.id || b.data.slug, target_name: b.data.name, page: `/app/brand/${slug}` });
        try {
          const p = await axios.get(`${API}/products?brand=${encodeURIComponent(b.data.name)}`);
          setProducts((p.data || []).slice(0, 12));
        } catch { /* noop */ }
      } catch {
        setError("Бренд не найден");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div style={{ background: "var(--ang-bg)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "13px" }}>
        Загрузка…
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div style={{ background: "var(--ang-bg)", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px", padding: "20px", textAlign: "center" }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "var(--ang-text)" }}>Бренд не найден</div>
        <button onClick={() => navigate("/app/catalog")}
          style={{ background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", border: "none", borderRadius: "4px", padding: "11px 22px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
          В каталог
        </button>
      </div>
    );
  }

  return (
    <div data-testid="brand-detail-page" style={{ background: "var(--ang-bg)", minHeight: "100vh", color: "var(--ang-text)" }}>
      {/* Back */}
      <button onClick={() => navigate(-1)} data-testid="brand-back-btn"
        style={{ position: "fixed", top: "14px", left: "10px", background: "rgba(10,10,10,0.7)", backdropFilter: "blur(8px)", border: "1px solid var(--ang-border)", borderRadius: "999px", padding: "8px 14px 8px 10px", cursor: "pointer", color: "var(--ang-text)", display: "flex", alignItems: "center", gap: "4px", fontFamily: "Manrope", fontSize: "12px", zIndex: 10 }}>
        <ChevronLeft size={16} /> Назад
      </button>

      {/* Cover */}
      <div style={{ position: "relative", width: "100%", height: "320px", overflow: "hidden", background: "var(--ang-surface)" }}>
        {brand.cover_image ? (
          <img src={resolveImageUrl(brand.cover_image, { w: 1200 })} alt={brand.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, var(--ang-surface) 0%, #2A1F0A 100%)" }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,10,10,0.05) 0%, rgba(10,10,10,0.55) 60%, var(--ang-bg) 100%)" }} />

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          style={{ position: "absolute", left: 20, right: 20, bottom: 22 }}>
          <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.3em", color: "var(--ang-gold-strong)", textTransform: "uppercase", marginBottom: "6px" }}>
            BRAND
          </div>
          <h1 data-testid="brand-name" style={{ fontFamily: "Cormorant Garamond", fontSize: "40px", fontWeight: 500, lineHeight: 1, marginBottom: "10px", letterSpacing: "0.01em" }}>
            {brand.name}
          </h1>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", color: "var(--ang-text-soft)", fontFamily: "Manrope", fontSize: "12px" }}>
            {brand.country && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <MapPin size={12} style={{ color: "var(--ang-gold)" }} /> {brand.country}
              </span>
            )}
            {brand.founded_year && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Calendar size={12} style={{ color: "var(--ang-gold)" }} /> с {brand.founded_year}
              </span>
            )}
            {brand.products_count > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Award size={12} style={{ color: "var(--ang-gold)" }} /> {brand.products_count} товаров
              </span>
            )}
          </div>
        </motion.div>
      </div>

      {/* Short / Long description */}
      {(brand.short_description || brand.long_description) && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
          style={{ padding: "26px 20px 8px" }}>
          {brand.short_description && (
            <div data-testid="brand-short-desc" style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", lineHeight: 1.4, fontWeight: 400, marginBottom: brand.long_description ? "14px" : 0, fontStyle: "italic" }}>
              «{brand.short_description}»
            </div>
          )}
          {brand.long_description && (
            <div data-testid="brand-long-desc">
              <MarkdownContent text={brand.long_description} style={{ color: "var(--ang-text-soft)", fontSize: "14px" }} />
            </div>
          )}
        </motion.div>
      )}

      {/* Facts */}
      {brand.facts && brand.facts.length > 0 && (
        <div style={{ padding: "22px 16px 8px" }}>
          <div style={{ padding: "0 4px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "24px", height: "1px", background: "var(--ang-gold)" }} />
            <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.25em", color: "var(--ang-gold)", textTransform: "uppercase", fontWeight: 700 }}>
              О бренде
            </div>
          </div>
          <div data-testid="brand-facts" style={{ display: "grid", gap: "10px" }}>
            {brand.facts.map((fact, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: idx * 0.06 }}
                style={{ background: "linear-gradient(135deg, var(--ang-surface-2) 0%, var(--ang-surface) 100%)", border: "1px solid var(--ang-gold-soft)", borderRadius: "8px", padding: "16px 16px 16px 12px", display: "flex", gap: "14px", alignItems: "flex-start" }}>
                <div style={{ flexShrink: 0, width: "34px", height: "34px", borderRadius: "50%", background: "linear-gradient(135deg, var(--ang-gold), var(--ang-gold-2))", color: "var(--ang-bg)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Cormorant Garamond", fontWeight: 700, fontSize: "16px" }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Cormorant Garamond", fontSize: "17px", color: "var(--ang-text)", fontWeight: 500, marginBottom: fact.description ? "4px" : 0, lineHeight: 1.3 }}>
                    {fact.title}
                  </div>
                  {fact.description && (
                    <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "var(--ang-text-muted)", lineHeight: 1.5 }}>
                      {fact.description}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Factory gallery */}
      {brand.factory_images && brand.factory_images.length > 0 && (
        <div style={{ padding: "22px 16px 8px" }}>
          <div style={{ padding: "0 4px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "24px", height: "1px", background: "var(--ang-gold)" }} />
            <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.25em", color: "var(--ang-gold)", textTransform: "uppercase", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
              <ImageIcon size={12} /> Производство
            </div>
          </div>
          <div data-testid="brand-factory" style={{ display: "flex", gap: "10px", overflowX: "auto", paddingBottom: "6px", scrollbarWidth: "none" }}>
            {brand.factory_images.map((url, idx) => (
              <div key={idx} onClick={() => setLightbox(url)} data-testid={`factory-img-${idx}`}
                style={{ flexShrink: 0, width: "220px", aspectRatio: "3/4", borderRadius: "6px", overflow: "hidden", cursor: "zoom-in", background: "var(--ang-surface)", border: "1px solid var(--ang-border)" }}>
                <img src={resolveImageUrl(url, { w: 600 })} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Certificates */}
      {brand.certificates && brand.certificates.length > 0 && (
        <div style={{ padding: "22px 16px 8px" }}>
          <div style={{ padding: "0 4px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "24px", height: "1px", background: "var(--ang-gold)" }} />
            <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.25em", color: "var(--ang-gold)", textTransform: "uppercase", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldCheck size={12} /> Документы и сертификаты
            </div>
          </div>
          <div data-testid="brand-certificates" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
            {brand.certificates.map((url, idx) => (
              <div key={idx} onClick={() => setLightbox(url)} data-testid={`cert-img-${idx}`}
                style={{ aspectRatio: "3/4", borderRadius: "6px", overflow: "hidden", cursor: "zoom-in", background: "var(--ang-surface)", border: "1px solid var(--ang-border)" }}>
                <img src={resolveImageUrl(url, { w: 600 })} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brand products */}
      {products.length > 0 && (
        <div style={{ padding: "22px 16px 8px" }}>
          <div style={{ padding: "0 4px", marginBottom: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ width: "24px", height: "1px", background: "var(--ang-gold)" }} />
              <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.25em", color: "var(--ang-gold)", textTransform: "uppercase", fontWeight: 700 }}>
                Товары бренда
              </div>
            </div>
            <button data-testid="brand-all-products"
              onClick={() => navigate(`/app/catalog?brand=${encodeURIComponent(brand.name)}`)}
              style={{ background: "none", border: "none", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
              Все <ChevronRight size={14} />
            </button>
          </div>
          <div data-testid="brand-products-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            {products.map((p) => (
              <div key={p.id} onClick={() => navigate(`/app/catalog/${p.id}`)} data-testid={`brand-product-${p.id}`}
                style={{ background: "var(--ang-surface)", borderRadius: "6px", overflow: "hidden", cursor: "pointer", border: "1px solid var(--ang-border)" }}>
                <div style={{ position: "relative", aspectRatio: "9/16", background: "var(--ang-surface-2)" }}>
                  {p.images?.[0] && <img src={resolveImageUrl(p.images[0], { w: 600 })} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  {p.discount && (
                    <div style={{ position: "absolute", top: "8px", right: "8px", background: "#E11D2A", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "11px", fontWeight: 900, padding: "3px 7px", borderRadius: "3px" }}>
                      -{p.discount}%
                    </div>
                  )}
                </div>
                <div style={{ padding: "10px" }}>
                  <div style={{ fontFamily: "Cormorant Garamond", fontSize: "13px", color: "var(--ang-text)", lineHeight: 1.3, marginBottom: "4px" }}>
                    {p.name}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" }}>
                    {p.old_price && <span style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-faint)", textDecoration: "line-through" }}>{Number(p.old_price).toLocaleString("ru-RU")} ₸</span>}
                    <span style={{ fontFamily: "Manrope", fontSize: "13px", color: p.old_price ? "#E11D2A" : "var(--ang-gold)", fontWeight: 700 }}>{Number(p.price).toLocaleString("ru-RU")} ₸</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <div style={{ padding: "26px 16px 40px" }}>
        <button data-testid="brand-cta-catalog"
          onClick={() => navigate(`/app/catalog?brand=${encodeURIComponent(brand.name)}`)}
          style={{ width: "100%", background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", border: "none", borderRadius: "6px", padding: "16px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase" }}>
          Смотреть все товары {brand.name}
        </button>
      </div>

      <PhotoLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
